/**
 * Company name detector.
 * Scans bullet/headline text for known brand mentions and returns the best matching slug.
 */
import { brandIconMap } from './brandIcons';
import reelData from '../../public/reel-data.json';

const data = reelData as any;

/** Common company name → brand slug mapping */
const companyAliases: Record<string, string> = {
  // AI Companies
  'openai': 'openai',
  'open ai': 'openai',
  'chatgpt': 'openai',
  'gpt-4': 'openai',
  'gpt-5': 'openai',
  'gpt': 'openai',
  'dall-e': 'openai',
  'anthropic': 'anthropic',
  'claude': 'anthropic',
  'google': 'google',
  'google ai': 'google',
  'gemini': 'google',
  'deepmind': 'google',
  'bard': 'google',
  'alphabet': 'google',
  'meta': 'meta',
  'meta ai': 'meta',
  'llama': 'meta',
  'nvidia': 'nvidia',
  'hugging face': 'huggingface',
  'huggingface': 'huggingface',
  'xai': 'x',
  'grok': 'x',

  // Big Tech
  'apple': 'apple',
  'iphone': 'apple',
  'ipad': 'apple',
  'macbook': 'apple',
  'siri': 'apple',
  'samsung': 'samsung',
  'intel': 'intel',
  'amd': 'amd',
  'qualcomm': 'qualcomm',
  'ibm': 'ibm',
  'watson': 'ibm',
  'oracle': 'oracle',
  'cisco': 'cisco',
  'dell': 'dell',

  // Cloud
  'aws': 'amazonwebservices',
  'amazon web services': 'amazonwebservices',
  'amazon': 'amazon',
  'aws s3': 'amazonwebservices',
  'google cloud': 'googlecloud',
  'gcp': 'googlecloud',
  'azure': 'microsoftazure',
  'microsoft azure': 'microsoftazure',
  'microsoft': 'microsoft',
  'windows': 'microsoft',
  'bing': 'microsoft',
  'cloudflare': 'cloudflare',
  'vercel': 'vercel',
  'netlify': 'netlify',
  'digitalocean': 'digitalocean',
  'digital ocean': 'digitalocean',
  'heroku': 'heroku',
  'firebase': 'firebase',

  // Social
  'twitter': 'x',
  'x.com': 'x',
  'instagram': 'instagram',
  'tiktok': 'tiktok',
  'tik tok': 'tiktok',
  'youtube': 'youtube',
  'linkedin': 'linkedin',
  'reddit': 'reddit',
  'discord': 'discord',
  'twitch': 'twitch',
  'snapchat': 'snapchat',
  'pinterest': 'pinterest',
  'threads': 'threads',
  'facebook': 'facebook',
  'whatsapp': 'whatsapp',
  'telegram': 'telegram',
  'signal': 'signal',

  // Dev Tools
  'github': 'github',
  'gitlab': 'gitlab',
  'docker': 'docker',
  'kubernetes': 'kubernetes',
  'k8s': 'kubernetes',
  'stack overflow': 'stackoverflow',
  'stackoverflow': 'stackoverflow',
  'npm': 'npm',
  'node': 'nodedotjs',
  'node.js': 'nodedotjs',
  'nodejs': 'nodedotjs',
  'python': 'python',
  'rust': 'rust',
  'typescript': 'typescript',

  // Crypto & Finance
  'bitcoin': 'bitcoin',
  'btc': 'bitcoin',
  'ethereum': 'ethereum',
  'eth': 'ethereum',
  'coinbase': 'coinbase',
  'binance': 'binance',
  'stripe': 'stripe',
  'paypal': 'paypal',
  'visa': 'visa',
  'mastercard': 'mastercard',
  'robinhood': 'robinhood',
  'cash app': 'cashapp',
  'cashapp': 'cashapp',

  // Software & Services
  'spotify': 'spotify',
  'netflix': 'netflix',
  'uber': 'uber',
  'airbnb': 'airbnb',
  'shopify': 'shopify',
  'salesforce': 'salesforce',
  'adobe': 'adobe',
  'photoshop': 'adobe',
  'figma': 'figma',
  'notion': 'notion',
  'slack': 'slack',
  'zoom': 'zoom',
  'dropbox': 'dropbox',
  'atlassian': 'atlassian',
  'jira': 'jira',

  // Gaming
  'steam': 'steam',
  'epic games': 'epicgames',
  'epic': 'epicgames',
  'playstation': 'playstation',
  'ps5': 'playstation',
  'xbox': 'xbox',
  'nintendo': 'nintendo',
  'roblox': 'roblox',
  'unity': 'unity',

  // Automotive & Space
  'tesla': 'tesla',
  'spacex': 'spacex',
  'space x': 'spacex',
  'starship': 'spacex',

  // News & Browser
  'google news': 'googlenews',
  'brave': 'brave',
  'brave browser': 'brave',
};

/**
 * Detect the most prominent brand mentioned in a text string.
 * Returns the brand slug (usable as a key in brandIconMap), or null if none found.
 *
 * Uses a greedy longest-match strategy — "Amazon Web Services" beats "Amazon".
 */
export function detectBrandInText(text: string): string | null {
  const lower = text.toLowerCase();
  let bestMatch: string | null = null;
  let bestLen = 0;

  for (const [alias, slug] of Object.entries(companyAliases)) {
    // Only match if the brand is in our icon map
    if (!brandIconMap[slug]) continue;

    const idx = lower.indexOf(alias.toLowerCase());
    if (idx !== -1 && alias.length > bestLen) {
      bestMatch = slug;
      bestLen = alias.length;
    }
  }

  // Fallback 1: Check article keywords
  if (!bestMatch && data && Array.isArray(data.keywords)) {
    for (const keyword of data.keywords) {
      const lowerKeyword = keyword.toLowerCase().trim();
      for (const [alias, slug] of Object.entries(companyAliases)) {
        if (!brandIconMap[slug]) continue;
        if (lowerKeyword === alias.toLowerCase() || lowerKeyword.includes(alias.toLowerCase())) {
          return slug;
        }
      }
    }
  }

  // Fallback 2: Check article headline
  if (!bestMatch && data && data.headline) {
    const lowerHeadline = data.headline.toLowerCase();
    for (const [alias, slug] of Object.entries(companyAliases)) {
      if (!brandIconMap[slug]) continue;
      const idx = lowerHeadline.indexOf(alias.toLowerCase());
      if (idx !== -1 && alias.length > bestLen) {
        bestMatch = slug;
        bestLen = alias.length;
      }
    }
  }

  return bestMatch;
}

/**
 * Detect all brands mentioned in a text string (deduped, ordered by position).
 */
export function detectAllBrandsInText(text: string): string[] {
  const lower = text.toLowerCase();
  const found = new Map<number, string>(); // position → slug

  for (const [alias, slug] of Object.entries(companyAliases)) {
    if (!brandIconMap[slug]) continue;
    const idx = lower.indexOf(alias.toLowerCase());
    if (idx !== -1) {
      // Keep longest match at same position
      const existing = found.get(idx);
      if (!existing || alias.length > (existing.length ?? 0)) {
        found.set(idx, slug);
      }
    }
  }

  // Sort by position, dedupe slugs
  const slugs = [...new Set([...found.entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s))];
  return slugs;
}

/**
 * Get the initials for a company name (used as monogram fallback).
 * e.g. "OpenAI" → "OA", "Amazon Web Services" → "AW"
 */
export function getMonogram(text: string): string {
  const words = text.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
